import React, { useState } from 'react';
import './UTFunctionsModal.css';

// Custom "Functions" flow for the UT spreadsheet.
//
// MIN / MAX / SUM / AVERAGE: 2 ranges
//   range1 (target) = single cell, single row, or single column
//   range2 (source) = >1 cell; size must match range1's "open" dimension
//   1x1 special case: range2 may be any range
//
// Degradation: 3 ranges
//   range1 (target) = any size/shape
//   range2 (nominal) = 1x1, a single column matching range1's row count,
//                       a single row matching range1's column count, or the
//                       exact same shape as range1
//   range3 (avg thk) = same shape as range1
//   Formula evaluates to (nominal - avg)/nominal as a percent (1dp), or "-" if
//   any input is non-numeric. The cell is percent-formatted so the web app
//   recognises it.
//
//   Shared-nominal `*` resolution: at plan time we read each chosen nominal
//   cell's value via wb.getCellValue. If the cell is blank or '*', we walk up
//   the same column toward the top of range2 until we hit a numeric, and the
//   formula gets a direct ref to that resolved cell. If no numeric is found,
//   IFERROR collapses the output to "-".
//
// After writing the formulas, a heading is auto-added in the cell directly
// to the left (row range) or above (column range) of range1, but only if
// that cell exists and is empty. 2D output ranges get no heading.

const FUNCTIONS = [
  { key: 'MINMAXAVG',   label: 'MIN / MAX / AVG', heading: '' },
  { key: 'MIN',         label: 'MIN',         heading: 'Lowest thk' },
  { key: 'MAX',         label: 'MAX',         heading: 'Highest thk' },
  { key: 'SUM',         label: 'SUM',         heading: 'Sum of thk' },
  { key: 'AVERAGE',     label: 'AVERAGE',     heading: 'Average thk' },
  { key: 'DEGRADATION', label: 'DEGRADATION', heading: 'Average Degradation' },
];

const HEADING_OF = {
  MIN:     'Lowest thk',
  MAX:     'Highest thk',
  AVERAGE: 'Average thk',
};

// 0 → A, 25 → Z, 26 → AA, …
function colLetter(c) {
  let s = '';
  let n = c;
  while (n >= 0) {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
  }
  return s;
}
function a1(r, c) { return `${colLetter(c)}${r + 1}`; }
function rangeRef(r1, c1, r2, c2) {
  if (r1 === r2 && c1 === c2) return a1(r1, c1);
  return `${a1(r1, c1)}:${a1(r2, c2)}`;
}

function selBounds(sel) {
  const [r0, r1] = sel.row;
  const [c0, c1] = sel.column;
  return {
    rTop: Math.min(r0, r1),
    rBot: Math.max(r0, r1),
    cLeft: Math.min(c0, c1),
    cRight: Math.max(c0, c1),
    rows: Math.abs(r1 - r0) + 1,
    cols: Math.abs(c1 - c0) + 1,
  };
}

// Where the heading cell sits relative to range1.
//  - row range  → left of range1
//  - col range  → above range1
//  - else       → no heading
function headingPos(r1) {
  if (r1.rows === 1 && r1.cols > 1) {
    return r1.cLeft - 1 >= 0 ? { r: r1.rTop, c: r1.cLeft - 1 } : null;
  }
  if (r1.cols === 1 && r1.rows > 1) {
    return r1.rTop - 1 >= 0 ? { r: r1.rTop - 1, c: r1.cLeft } : null;
  }
  return null;
}

// Plan ops for MIN/MAX/SUM/AVERAGE.
function planSimple(fn, sels) {
  if (sels.length < 2) {
    return { error: 'Select range 1 (target), then hold Ctrl and select range 2 (source).' };
  }
  const r1 = selBounds(sels[0]);
  const r2 = selBounds(sels[1]);
  if (r1.rows > 1 && r1.cols > 1) {
    return { error: 'Range 1 must be a single cell, a single row, or a single column.' };
  }
  if (r2.rows === 1 && r2.cols === 1) {
    return { error: 'Range 2 must be a range, not a single cell.' };
  }

  const writes = [];
  if (r1.rows === 1 && r1.cols === 1) {
    const ref = rangeRef(r2.rTop, r2.cLeft, r2.rBot, r2.cRight);
    writes.push({ r: r1.rTop, c: r1.cLeft, formula: `=${fn}(${ref})` });
  } else if (r1.rows === 1) {
    if (r2.cols !== r1.cols) {
      return { error: `Range 1 has ${r1.cols} columns; range 2 must have ${r1.cols} columns (got ${r2.cols}).` };
    }
    for (let i = 0; i < r1.cols; i++) {
      const c = r2.cLeft + i;
      const ref = rangeRef(r2.rTop, c, r2.rBot, c);
      writes.push({ r: r1.rTop, c: r1.cLeft + i, formula: `=${fn}(${ref})` });
    }
  } else {
    if (r2.rows !== r1.rows) {
      return { error: `Range 1 has ${r1.rows} rows; range 2 must have ${r1.rows} rows (got ${r2.rows}).` };
    }
    for (let i = 0; i < r1.rows; i++) {
      const r = r2.rTop + i;
      const ref = rangeRef(r, r2.cLeft, r, r2.cRight);
      writes.push({ r: r1.rTop + i, c: r1.cLeft, formula: `=${fn}(${ref})` });
    }
  }
  return { writes, target: r1 };
}

// Plan ops for the combined MIN / MAX / AVG function.
// Same range1/range2 selection rules as planSimple. Writes three parallel
// outputs: MIN at range1, then MAX and AVERAGE shifted by one cell each in
// range1's "open" direction.
//   - range1 column (rows match)  → MAX one column right, AVG two right
//   - range1 row    (cols match)  → MAX one row below,  AVG two below
//   - range1 1x1                  → treated as column-output (MAX/AVG to right)
// Each output gets a heading written in the existing heading slot (above for
// column outputs, left of for row outputs), subject to the same empty-cell
// guard used for single-function plans.
function planMinMaxAvg(sels) {
  if (sels.length < 2) {
    return { error: 'Select range 1 (target), then hold Ctrl and select range 2 (source).' };
  }
  const r1 = selBounds(sels[0]);
  const r2 = selBounds(sels[1]);
  if (r1.rows > 1 && r1.cols > 1) {
    return { error: 'Range 1 must be a single cell, a single row, or a single column.' };
  }
  if (r2.rows === 1 && r2.cols === 1) {
    return { error: 'Range 2 must be a range, not a single cell.' };
  }
  // 1x1 r1 → column-output (MAX/AVG laid out to the right).
  const rowOutput = r1.rows === 1 && r1.cols > 1; // true when r1 is a row
  if (rowOutput && r2.cols !== r1.cols) {
    return { error: `Range 1 has ${r1.cols} columns; range 2 must have ${r1.cols} columns (got ${r2.cols}).` };
  }
  if (!rowOutput && r1.rows > 1 && r2.rows !== r1.rows) {
    return { error: `Range 1 has ${r1.rows} rows; range 2 must have ${r1.rows} rows (got ${r2.rows}).` };
  }

  const fns = ['MIN', 'MAX', 'AVERAGE'];
  const writes = [];
  const headings = [];

  for (let k = 0; k < fns.length; k++) {
    const fn = fns[k];
    const dr = rowOutput ? k : 0;
    const dc = rowOutput ? 0 : k;
    const outRTop  = r1.rTop  + dr;
    const outCLeft = r1.cLeft + dc;

    if (r1.rows === 1 && r1.cols === 1) {
      const ref = rangeRef(r2.rTop, r2.cLeft, r2.rBot, r2.cRight);
      writes.push({ r: outRTop, c: outCLeft, formula: `=${fn}(${ref})` });
    } else if (rowOutput) {
      for (let i = 0; i < r1.cols; i++) {
        const c = r2.cLeft + i;
        const ref = rangeRef(r2.rTop, c, r2.rBot, c);
        writes.push({ r: outRTop, c: outCLeft + i, formula: `=${fn}(${ref})` });
      }
    } else {
      for (let i = 0; i < r1.rows; i++) {
        const r = r2.rTop + i;
        const ref = rangeRef(r, r2.cLeft, r, r2.cRight);
        writes.push({ r: outRTop + i, c: outCLeft, formula: `=${fn}(${ref})` });
      }
    }

    // Heading position for this output band — same offsets as headingPos.
    if (rowOutput) {
      // row → heading to the left of the output row's first cell
      if (outCLeft - 1 >= 0) {
        headings.push({ r: outRTop, c: outCLeft - 1, text: HEADING_OF[fn] });
      }
    } else {
      // column or 1x1 → heading above the output column's first cell
      if (outRTop - 1 >= 0) {
        headings.push({ r: outRTop - 1, c: outCLeft, text: HEADING_OF[fn] });
      }
    }
  }

  return { writes, headings };
}

// Read a cell's primitive value (handles FortuneSheet object cells).
function readCellRaw(wb, r, c) {
  let v;
  try { v = wb.getCellValue(r, c); } catch { return null; }
  if (v === null || v === undefined) return null;
  if (typeof v === 'object') {
    if (v.v !== undefined && v.v !== null && v.v !== '') return v.v;
    if (v.m !== undefined && v.m !== null && v.m !== '') return v.m;
    return null;
  }
  if (v === '') return null;
  return v;
}

// Resolve a nominal cell. Walks up the column from (nomR, nomC) toward topR,
// skipping blanks and '*' placeholders, until a numeric cell is found. Returns
// the row of the resolved cell, or nomR if nothing numeric is found above (the
// formula's IFERROR will then collapse the result to "-").
function resolveNominalRow(wb, nomR, nomC, topR) {
  for (let r = nomR; r >= topR; r--) {
    const raw = readCellRaw(wb, r, nomC);
    if (raw === null) continue;
    const s = String(raw).trim();
    if (s === '' || s === '*') continue;
    if (!Number.isNaN(Number(s))) return r;
  }
  return nomR;
}

// Plan ops for Degradation.
function planDegradation(sels, wb) {
  if (sels.length < 3) {
    return { error: 'Select range 1 (output), then Ctrl-select range 2 (nominal), then Ctrl-select range 3 (average thk).' };
  }
  const r1 = selBounds(sels[0]);
  const r2 = selBounds(sels[1]);
  const r3 = selBounds(sels[2]);

  if (r1.rows !== r3.rows || r1.cols !== r3.cols) {
    return { error: `Range 1 (${r1.rows}x${r1.cols}) and range 3 (${r3.rows}x${r3.cols}) must have the exact same size.` };
  }

  const r2IsCell  = r2.rows === 1 && r2.cols === 1;
  const r2IsCol   = r2.cols === 1 && r2.rows > 1;
  const r2IsRow   = r2.rows === 1 && r2.cols > 1;
  const r2IsBlock = r2.rows > 1 && r2.cols > 1;

  if (r2IsCol && r2.rows !== r1.rows) {
    return { error: `Range 2 is a column of ${r2.rows} cells; it must match range 1's row count (${r1.rows}).` };
  }
  if (r2IsRow && r2.cols !== r1.cols) {
    return { error: `Range 2 is a row of ${r2.cols} cells; it must match range 1's column count (${r1.cols}).` };
  }
  if (r2IsBlock && (r2.rows !== r1.rows || r2.cols !== r1.cols)) {
    return { error: `Range 2 (${r2.rows}x${r2.cols}) and range 1 (${r1.rows}x${r1.cols}) must have the exact same size, or range 2 must be a single cell, single row, or single column.` };
  }

  const writes = [];
  for (let i = 0; i < r1.rows; i++) {
    for (let j = 0; j < r1.cols; j++) {
      const outR = r1.rTop + i;
      const outC = r1.cLeft + j;
      let nomR, nomC;
      if (r2IsCell)      { nomR = r2.rTop;     nomC = r2.cLeft; }
      else if (r2IsCol)  { nomR = r2.rTop + i; nomC = r2.cLeft; }
      else if (r2IsRow)  { nomR = r2.rTop;     nomC = r2.cLeft + j; }
      else               { nomR = r2.rTop + i; nomC = r2.cLeft + j; }
      const avgR = r3.rTop + i;
      const avgC = r3.cLeft + j;
      // Resolve '*' shared-nominal placeholders by walking up the column
      // at plan time and emitting a direct cell ref to the actual numeric.
      const resolvedNomR = resolveNominalRow(wb, nomR, nomC, r2.rTop);
      const N = a1(resolvedNomR, nomC);
      const A = a1(avgR, avgC);
      // FortuneSheet's IF eagerly evaluates BOTH branches, so wrapping the
      // divide in an IF(ISNUMBER…) guard still yields #VALUE! when nominal
      // is "-". Skip the IF entirely: do the divide directly and let
      // IFERROR catch every failure mode at once — non-numeric N, non-numeric
      // A, division by zero, '*' anchor with no numeric above — all collapse
      // to "-".
      const formula = `=IFERROR((${N}-${A})/(${N}),"-")`;
      writes.push({ r: outR, c: outC, formula, percent: true });
    }
  }
  return { writes, target: r1 };
}

export default function UTFunctionsModal({ workbookRef, onCancel }) {
  const [picked, setPicked] = useState('MINMAXAVG');
  const [error, setError] = useState('');

  function handleOk() {
    setError('');
    const wb = workbookRef.current;
    if (!wb) {
      setError('Spreadsheet not ready.');
      return;
    }
    const sels = wb.getSelection?.();
    if (!Array.isArray(sels) || sels.length < 1) {
      setError('Select a range first.');
      return;
    }
    const fn = FUNCTIONS.find((f) => f.key === picked);
    const plan = picked === 'DEGRADATION' ? planDegradation(sels, wb)
      : picked === 'MINMAXAVG'           ? planMinMaxAvg(sels)
      : planSimple(picked, sels);
    if (plan.error) {
      setError(plan.error);
      return;
    }

    try {
      // 1) Write formulas.
      for (const w of plan.writes) {
        wb.setCellValue(w.r, w.c, w.formula);
      }
      // 2) For Degradation, percent-format every output cell so the value
      //    renders as e.g. 12.3% in the spreadsheet AND the web app picks
      //    it up as a percent cell.
      for (const w of plan.writes) {
        if (w.percent) {
          try {
            wb.setCellFormat(w.r, w.c, 'ct', { fa: '0.0%', t: 'n' });
          } catch { /* best effort */ }
        }
      }
      // 3) Headings — only when the target slot exists and is currently
      //    empty. Combo plans (MIN/MAX/AVG) supply a list directly; legacy
      //    single-target plans synthesise one entry from headingPos+fn.heading.
      const headings = plan.headings || (() => {
        const pos = headingPos(plan.target);
        return pos ? [{ r: pos.r, c: pos.c, text: fn.heading }] : [];
      })();
      for (const h of headings) {
        if (!h.text) continue;
        let existing = null;
        try { existing = wb.getCellValue(h.r, h.c); } catch { existing = null; }
        const isEmpty = existing === null || existing === undefined ||
          existing === '' ||
          (typeof existing === 'object' &&
            (existing.v === undefined || existing.v === null || existing.v === '') &&
            (existing.m === undefined || existing.m === null || existing.m === ''));
        if (isEmpty) {
          try {
            wb.setCellValue(h.r, h.c, h.text);
            wb.setCellFormat(h.r, h.c, 'bl', 1);
            // Stamp Arial (FortuneSheet font index 1) so headings match the
            // rest of the sheet — otherwise they default to Times New Roman.
            wb.setCellFormat(h.r, h.c, 'ff', 1);
          } catch { /* best effort */ }
        }
      }
    } catch (err) {
      setError(`Failed to write formulas: ${err?.message || 'unknown error'}`);
      return;
    }

    onCancel();
  }

  const helpText = picked === 'DEGRADATION'
    ? 'Select range 1 (output), then Ctrl-select range 2 (nominal), then Ctrl-select range 3 (average thickness).'
    : 'Select range 1 (where formulas go), then hold Ctrl and select range 2 (the source data).';

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal ut-fn-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="modal-title">Insert function</h2>
        <p className="ut-fn-help">{helpText} Pick a function and press OK.</p>

        <div className="ut-fn-grid">
          {FUNCTIONS.map((f) => (
            <button
              key={f.key}
              type="button"
              className={`ut-fn-tile ${picked === f.key ? 'active' : ''}`}
              onClick={() => setPicked(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {error && <div className="ut-fn-error">{error}</div>}

        <div className="modal-actions ut-fn-actions">
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" onClick={handleOk}>OK</button>
        </div>
      </div>
    </div>
  );
}
